import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAdmin } from '../../contexts/AdminContext';

const TrackingOrder: React.FC = () => {
  const { isLoggedIn, user } = useAuth();
  const { orders } = useAdmin();
  const [trackingNumber, setTrackingNumber] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState<string | null>(null);
  const [searchFocused, setSearchFocused] = useState(false);

  const userOrders = useMemo(() => {
    if (!user) return [];
    return orders
      .filter(order => order.userEmail === user.email)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [orders, user]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const num = trackingNumber.trim();
    if (!num) { setError('Please enter a tracking number.'); return; }
    window.open(`https://www.17track.net/en/track#nums=${encodeURIComponent(num)}`, '_blank');
    setError('');
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(text);
    setTimeout(() => setCopied(null), 2000);
  };

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'delivered':
        return { label: 'Delivered', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500', step: 3 };
      case 'shipping':
        return { label: 'In Transit', bg: 'bg-forest-50', text: 'text-forest-700', border: 'border-forest-200', dot: 'bg-forest-500', step: 2 };
      case 'payment_confirmed':
        return { label: 'Processing', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500', step: 1 };
      default:
        return { label: 'Pending', bg: 'bg-neutral-50', text: 'text-neutral-600', border: 'border-neutral-200', dot: 'bg-neutral-400', step: 0 };
    }
  };

  const stepsMeta = [
    { label: 'Order Placed', sub: 'Confirmed' },
    { label: 'Processing', sub: 'Preparing' },
    { label: 'Shipped', sub: 'In transit' },
    { label: 'Delivered', sub: 'Complete' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Breadcrumb — thin, utilitarian */}
      <div className="border-b border-neutral-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5">
          <nav className="flex items-center gap-1.5 text-xs tracking-wide">
            <Link to="/" className="text-neutral-400 hover:text-neutral-600 transition-colors uppercase">Home</Link>
            <span className="text-neutral-300">/</span>
            <span className="text-neutral-800 font-semibold uppercase">Track Order</span>
          </nav>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16">

        {/* ─── SEARCH SECTION ─── */}
        <div className="mb-10">
          <div className="mb-6">
            <h1 className="text-[28px] font-display font-bold text-neutral-900 tracking-tight leading-tight">
              Track Your Order
            </h1>
            <p className="text-neutral-500 mt-1.5 text-[15px]">
              Enter your tracking number to check delivery status in real-time.
            </p>
          </div>

          <form onSubmit={handleSearch}>
            <div className={`
              flex flex-col sm:flex-row border rounded-xl overflow-hidden transition-all duration-200
              ${searchFocused ? 'border-forest-400 shadow-[0_0_0_3px_rgba(37,99,235,0.08)]' : 'border-neutral-200'}
              ${error ? 'border-red-300 shadow-[0_0_0_3px_rgba(239,68,68,0.06)]' : ''}
            `}>
              <div className="flex items-center flex-1 px-4 bg-white">
                <svg className="w-[18px] h-[18px] text-neutral-400 flex-shrink-0 mr-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20 21v-2a4 4 0 00-3-3.87M9 11V9m0 2a4 4 0 01-4-4V5a4 4 0 014-4h0a4 4 0 014 4v2a4 4 0 01-4 4zm-4.5 8h9" />
                </svg>
                <input
                  type="text"
                  value={trackingNumber}
                  onChange={(e) => { setTrackingNumber(e.target.value.toUpperCase()); setError(''); }}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setSearchFocused(false)}
                  className="flex-1 py-3.5 text-[15px] text-neutral-900 placeholder-neutral-400 focus:outline-none bg-transparent font-mono tracking-wider"
                  placeholder="e.g. LP080941138IN"
                  spellCheck={false}
                  autoComplete="off"
                />
              </div>
              <button
                type="submit"
                className="px-7 py-3.5 bg-forest-400 text-white text-sm font-semibold hover:bg-forest-500 active:bg-forest-500 transition-colors cursor-pointer whitespace-nowrap border-t sm:border-t-0 sm:border-l border-forest-400/20"
              >
                Track Package
              </button>
            </div>
            {error && (
              <p className="mt-2.5 text-[13px] text-red-600 flex items-center gap-1.5 font-medium">
                <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 16 16" fill="currentColor">
                  <path fillRule="evenodd" d="M8 15A7 7 0 108 1a7 7 0 000 14zM8 4a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 018 4zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
                {error}
              </p>
            )}
            <p className="mt-2.5 text-xs text-neutral-400">
              Find your tracking number in the shipping confirmation email or your order history below.
            </p>
          </form>
        </div>

        {/* ─── MY ORDERS ─── */}
        {isLoggedIn && userOrders.length > 0 && (
          <div className="mb-10">
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="text-lg font-display font-bold text-neutral-900">
                Your Orders
              </h2>
              <span className="text-xs text-neutral-400 font-medium tabular-nums">
                {userOrders.length} order{userOrders.length !== 1 ? 's' : ''}
              </span>
            </div>

            <div className="space-y-3">
              {userOrders.map((order) => {
                const status = getStatusInfo(order.status);
                const currentStep = status.step;

                return (
                  <div
                    key={order.id}
                    className="border border-neutral-150 rounded-2xl overflow-hidden hover:border-neutral-300 transition-colors"
                  >
                    {/* ── Order Meta Bar ── */}
                    <div className="bg-neutral-50/80 border-b border-neutral-100 px-5 py-2.5 flex flex-wrap items-center gap-x-5 gap-y-1">
                      <div className="flex items-center gap-4 text-xs">
                        <div>
                          <span className="text-neutral-400 uppercase tracking-wider font-medium">Order</span>{' '}
                          <span className="text-neutral-800 font-bold tabular-nums">{order.orderNumber}</span>
                        </div>
                        <span className="text-neutral-200">|</span>
                        <span className="text-neutral-500">
                          {new Date(order.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                      </div>
                      <div className="ml-auto">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wider border ${status.bg} ${status.text} ${status.border}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
                          {status.label}
                        </span>
                      </div>
                    </div>

                    <div className="px-5 py-5">
                      {/* ── Items ── */}
                      <div className="text-sm text-neutral-600 mb-5 leading-relaxed">
                        {order.items.map((item, i) => (
                          <span key={i}>
                            <span className="text-neutral-800 font-medium">{item.productName}</span>
                            {item.quantity > 1 && (
                              <span className="text-neutral-400 text-xs ml-1">×{item.quantity}</span>
                            )}
                            {i < order.items.length - 1 && (
                              <span className="text-neutral-300 mx-2">·</span>
                            )}
                          </span>
                        ))}
                      </div>

                      {/* ── Progress Stepper ── */}
                      <div className="relative py-2 mb-5">
                        {/* Track line */}
                        <div className="absolute top-[22px] left-[16px] right-[16px] h-[2px]">
                          <div className="absolute inset-0 bg-neutral-100 rounded-full" />
                          <div
                            className="absolute top-0 left-0 h-full bg-forest-500 rounded-full"
                            style={{
                              width: `${(currentStep / (stepsMeta.length - 1)) * 100}%`,
                              transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                            }}
                          />
                        </div>

                        <div className="relative flex justify-between">
                          {stepsMeta.map((step, i) => {
                            const done = i < currentStep;
                            const active = i === currentStep;
                            const future = i > currentStep;

                            return (
                              <div key={step.label} className="flex flex-col items-center" style={{ width: `${100 / stepsMeta.length}%` }}>
                                {/* Node */}
                                <div className={`
                                  relative w-[34px] h-[34px] rounded-full flex items-center justify-center
                                  transition-all duration-500 ease-out
                                  ${done ? 'bg-forest-400' : ''}
                                  ${active ? 'bg-forest-400 ring-[5px] ring-forest-100' : ''}
                                  ${future ? 'bg-white border-2 border-neutral-200' : ''}
                                `}>
                                  {done ? (
                                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                  ) : active ? (
                                    <div className="w-2.5 h-2.5 bg-white rounded-full" />
                                  ) : (
                                    <div className="w-2 h-2 bg-neutral-200 rounded-full" />
                                  )}
                                </div>

                                {/* Label */}
                                <span className={`
                                  mt-2.5 text-[11px] font-semibold text-center leading-tight
                                  ${done || active ? 'text-neutral-800' : 'text-neutral-400'}
                                `}>
                                  {step.label}
                                </span>
                                <span className={`
                                  text-[10px] text-center
                                  ${done || active ? 'text-neutral-400' : 'text-neutral-300'}
                                `}>
                                  {step.sub}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* ── Tracking Footer ── */}
                      {order.trackingNumber ? (
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-4 border-t border-neutral-100">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <span className="text-xs text-neutral-400 uppercase tracking-wider font-medium flex-shrink-0">Tracking</span>
                            <code className="text-[13px] font-mono font-bold text-neutral-900 bg-neutral-50 border border-neutral-200 px-2.5 py-1 rounded tracking-wider truncate">
                              {order.trackingNumber}
                            </code>
                            <button
                              onClick={() => handleCopy(order.trackingNumber || '')}
                              className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded hover:bg-neutral-100 transition-colors cursor-pointer"
                              title="Copy"
                            >
                              {copied === order.trackingNumber ? (
                                <svg className="w-3.5 h-3.5 text-emerald-500" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                              ) : (
                                <svg className="w-3.5 h-3.5 text-neutral-400" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9.75a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
                                </svg>
                              )}
                            </button>
                          </div>
                          <button
                            onClick={() => window.open(`https://www.17track.net/en/track#nums=${encodeURIComponent(order.trackingNumber || '')}`, '_blank')}
                            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-white border border-neutral-200 rounded text-[13px] font-semibold text-neutral-700 hover:bg-neutral-50 hover:border-neutral-300 active:bg-neutral-100 transition-all cursor-pointer"
                          >
                            Track on 17TRACK
                            <svg className="w-3.5 h-3.5 text-neutral-400" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25" />
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <div className="pt-4 border-t border-neutral-100">
                          <div className="flex items-center gap-2 text-[13px] text-neutral-400">
                            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Tracking number will be assigned once your order ships.
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ─── SIGN-IN NUDGE ─── */}
        {!isLoggedIn && (
          <div className="mb-10 border border-dashed border-neutral-200 rounded-2xl p-5 flex items-start gap-4">
            <div className="w-9 h-9 rounded-full bg-neutral-100 flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-neutral-400" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-semibold text-neutral-800 mb-0.5">Sign in for order tracking</p>
              <p className="text-[13px] text-neutral-400 mb-2.5">View all your shipments and order history in one place.</p>
              <Link
                to="/login"
                className="inline-flex items-center gap-1 text-[13px] font-semibold text-forest-400 hover:text-forest-500 transition-colors"
              >
                Sign in
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                </svg>
              </Link>
            </div>
          </div>
        )}

        {/* ─── SHIPPING INFO ─── */}
        <div className="border-t border-neutral-100 pt-8">
          <h2 className="text-sm font-display font-bold text-neutral-900 uppercase tracking-wider mb-4">
            Shipping Information
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              {
                icon: (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
                  </svg>
                ),
                title: 'Worldwide Shipping',
                desc: 'EMS International Express to most countries.',
              },
              {
                icon: (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.07-.504 1.004-1.125a57.75 57.75 0 00-.39-2.26L18.9 9.3a2.25 2.25 0 00-2.022-1.3H15M2.25 14.25h5.25m5.25 0H15m0 0V5.625m0 8.625h3.375M15 5.625a2.625 2.625 0 00-2.625-2.625H5.625A2.625 2.625 0 003 5.625v8.625" />
                  </svg>
                ),
                title: '2–3 Weeks Delivery',
                desc: 'Standard transit time including customs clearance.',
              },
              {
                icon: (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
                  </svg>
                ),
                title: 'Need Help?',
                desc: 'Reach us at support@liberacure.com for support.',
              },
            ].map((card) => (
              <div key={card.title} className="bg-neutral-50/60 border border-neutral-150 rounded-2xl px-4 py-4 group hover:border-neutral-200 transition-colors">
                <div className="text-forest-400 mb-2.5">{card.icon}</div>
                <h3 className="text-[13px] font-bold text-neutral-800 mb-0.5">{card.title}</h3>
                <p className="text-xs text-neutral-400 leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrackingOrder;
